<template>
  <div v-if="allowed" class="tier-page">
    <header class="tier-heading glass-card">
      <div>
        <small>LABORATORY / TIER BOARD</small>
        <h1>游戏分级板</h1>
        <p>选择作品、完成分级，然后导出当前分级板。</p>
      </div>
      <span class="session-note"><i class="el-icon-document-checked"></i> 保存在此浏览器 · 不会同步到服务器</span>
    </header>

    <div class="tier-workflow glass-card">
      <div class="board-tabs" role="tablist" aria-label="分级板">
        <div v-for="tab in tabs" :key="tab.id" class="board-tab" :class="{ active: tab.id === activeTabId }">
          <button type="button" role="tab" :aria-selected="tab.id === activeTabId" :title="tab.title || '未命名分级'" @click="switchTab(tab.id)">{{ tab.title || '未命名分级' }} <span>{{ Object.keys(tab.items).length }}</span></button>
          <button v-if="tabs.length > 1" type="button" class="tab-close" :aria-label="`关闭${tab.title || '未命名分级'}`" title="删除此分级板" @click="removeTab(tab.id)">×</button>
        </div>
        <button type="button" class="add-tab" title="新建分级板" @click="addTab"><i class="el-icon-plus"></i> 新建</button>
      </div>
      <el-steps class="workflow-steps" :active="workflowStep" finish-status="success" align-center>
        <el-step title="添加作品"></el-step>
        <el-step title="分级"></el-step>
        <el-step title="保存图片"></el-step>
      </el-steps>
      <app-scrollbar ref="workflowBody" class="workflow-body">
    <section v-show="workflowStep === 0" class="finder">
      <div class="section-heading"><div><small>FIND WORKS</small><h2>添加作品</h2></div><span>站内目录 · 未收录 RJ 从 DLsite 获取</span></div>
      <label class="board-title">分级标题 <el-input v-model="board.title" size="small" maxlength="60" show-word-limit placeholder="输入分级标题"></el-input></label>
      <div class="filter-grid">
        <label>作品名称或译名<el-input v-model.trim="filters.name" size="small" clearable placeholder="搜索作品名称" @keyup.enter.native="search"></el-input></label>
        <label>作品编号或站内 ID<el-input v-model.trim="filters.gameId" size="small" clearable placeholder="RJ… / 站内 ID" @keyup.enter.native="search"></el-input></label>
        <label>社团<el-select v-model="filters.author" size="small" filterable clearable placeholder="搜索社团名"><el-option v-for="maker in makers" :key="maker.value" :label="maker.label" :value="maker.value"></el-option></el-select></label>
        <label>作品类型<el-select v-model="filters.type" size="small" filterable clearable placeholder="全部类型"><el-option v-for="type in types" :key="type.value" :label="type.label" :value="type.value"></el-option></el-select></label>
        <label>标签<el-cascader v-model="filters.tags" size="small" :options="genres" :props="tagProps" :show-all-levels="false" filterable clearable collapse-tags placeholder="选择标签，最多 5 项" @change="limitTags"></el-cascader></label>
        <label class="release-filter">发售日期
          <el-date-picker
            v-if="!isMobile"
            v-model="filters.releaseTime"
            type="daterange"
            size="small"
            align="right"
            unlink-panels
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始"
            end-placeholder="结束"
            :editable="false"
            :picker-options="pickerOptions"
            clearable
          ></el-date-picker>
          <div v-else class="mobile-date-range">
            <el-date-picker v-model="mobileStartDate" type="date" size="small" value-format="yyyy-MM-dd" placeholder="开始日期" :editable="false" clearable popper-class="tier-mobile-date-popper"></el-date-picker>
            <el-date-picker v-model="mobileEndDate" type="date" size="small" value-format="yyyy-MM-dd" placeholder="结束日期" :editable="false" clearable popper-class="tier-mobile-date-popper"></el-date-picker>
          </div>
          <div v-if="isMobile" class="mobile-date-shortcuts" aria-label="快捷日期范围">
            <el-button v-for="shortcut in pickerOptions.shortcuts" :key="shortcut.text" size="mini" plain @click="applyDateShortcut(shortcut.text)">{{ shortcut.text }}</el-button>
          </div>
        </label>
      </div>
      <div class="filter-actions">
        <el-button type="primary" size="small" icon="el-icon-search" :loading="searching" @click="search">搜索作品</el-button>
        <el-button size="small" @click="resetFilters">清除条件</el-button>
        <span v-if="displayTotal" class="result-count">找到 {{ displayTotal.toLocaleString() }} 部</span>
      </div>
      <div v-if="searched" class="results">
        <div class="results-toolbar">
          <strong>搜索结果</strong>
          <div>
            <el-button size="mini" :disabled="!displayResults.length || bulkAdding" @click="addCurrentPage">加入本页</el-button>
            <el-button size="mini" type="primary" plain :disabled="!displayTotal || bulkAdding" @click="addAllMatches">全部加入</el-button>
            <el-button v-if="bulkAdding" size="mini" @click="cancelBulk = true">停止添加 {{ bulkProgress }}/{{ resultTotal }}</el-button>
            <el-button size="mini" icon="el-icon-close" aria-label="关闭搜索结果" @click="closeResults">关闭</el-button>
          </div>
        </div>
        <app-scrollbar class="result-scroll"><div v-loading="searching" class="result-list">
          <div v-for="game in displayResults" :key="game.id" class="result-item">
            <img :src="game.cover" :alt="game.name" loading="lazy">
            <div class="result-info"><strong :title="game.name"><span v-if="game.external" class="external-badge">DLsite</span>{{ game.translatedName || game.name }}</strong><span :title="game.name">{{ game.name }}</span><small>{{ game.gameId }} · {{ game.external ? '未收录' : game.id }} · {{ game.author }}</small></div>
            <el-button size="mini" :disabled="Boolean(items[String(game.id)])" @click="addRows([game])">{{ items[String(game.id)] ? '已加入' : '加入' }}</el-button>
          </div>
          <div v-if="!displayResults.length && !searching" class="empty-results">没有找到符合条件的作品</div>
        </div></app-scrollbar>
        <el-pagination v-if="resultTotal > 24" small background layout="prev, pager, next" :page-size="24" :current-page="resultPage" :total="resultTotal" @current-change="fetchResults"></el-pagination>
      </div>
      <div class="selected-works">
        <div class="selected-heading"><strong>已选作品 <em>{{ totalAdded }}</em></strong><span>可移除不想参与分级的作品</span></div>
        <app-scrollbar class="selected-scroll">
          <div v-if="!totalAdded" class="selected-empty">还没有加入作品</div>
          <div v-else class="selected-grid">
            <div v-for="id in Object.keys(items)" :key="id" class="rank-card" :title="cardTitle(items[id])">
              <img :src="items[id].cover" :alt="items[id].name" loading="lazy"><span>{{ items[id].translatedName || items[id].name }}</span><button type="button" :aria-label="`移除${items[id].name}`" title="移除作品" @click="removeGame(id)">×</button>
            </div>
          </div>
        </app-scrollbar>
      </div>
    </section>

    <section v-show="workflowStep === 1" class="board">
      <div class="section-heading board-heading">
        <div><small>YOUR RANKING</small><h2>分级区 <em>{{ rankedCount }} 部</em></h2></div>
        <div class="board-actions"><el-button size="small" @click="addTier">添加等级</el-button><el-button size="small" :disabled="!rankedCount" @click="resetPlacements">全部移回候选区</el-button><el-button size="small" type="danger" plain :disabled="!totalAdded" @click="clearBoard">重选作品</el-button></div>
      </div>
      <p v-if="storageError" class="storage-error">浏览器存储空间不足，当前更改未能保存。请减少分级板中的作品后重试。</p>
      <p v-if="selectedId" class="selection-hint">已选中 {{ items[selectedId] && (items[selectedId].translatedName || items[selectedId].name) }}，点击等级区域即可移动。<button type="button" @click="selectedId = ''">取消选择</button></p>
      <div class="tier-rows">
        <div v-for="tier in tiers" :key="tier.id" class="tier-row" :style="{ '--tier-color': tier.color }">
          <div class="tier-label" @click="placeSelected(tier.id)">
            <input v-model.trim="tier.label" maxlength="12" aria-label="等级名称" @click.stop>
            <input v-model="tier.color" type="color" aria-label="等级颜色" @click.stop>
            <button type="button" title="移除该等级，作品返回候选区" aria-label="移除等级" @click.stop="removeTier(tier.id)">×</button>
          </div>
          <div class="tier-drop" @click="placeSelected(tier.id)" @dragover.prevent @drop.prevent="dropOn($event, tier.id)">
            <span v-if="!placements[tier.id].length" class="drop-placeholder">拖入作品或点击此处放入选中作品</span>
            <div v-for="id in placements[tier.id]" :key="id" class="rank-card" :class="{ selected: selectedId === id }" draggable="true" :title="cardTitle(items[id])" @click.stop="selectGame(id)" @dragstart="startDrag($event, id)" @dragend="draggingId = ''" @dragover.prevent @drop.prevent.stop="dropOn($event, tier.id, id)">
              <img :src="items[id].cover" :alt="items[id].name" loading="lazy"><span>{{ items[id].translatedName || items[id].name }}</span><button type="button" aria-label="移回待分级" title="移回待分级" @click.stop="moveGame(id, 'pool')">×</button>
            </div>
          </div>
        </div>
      </div>
      <div class="pool-heading"><div><strong>待分级</strong><span>{{ placements.pool.length }} 部作品</span></div><span>拖到等级中，或点选作品后点等级</span></div>
      <div class="pool-drop" @click="placeSelected('pool')" @dragover.prevent @drop.prevent="dropOn($event, 'pool')">
        <span v-if="!placements.pool.length" class="pool-empty">从上方搜索并加入作品</span>
        <div v-for="id in visiblePool" :key="id" class="rank-card" :class="{ selected: selectedId === id }" draggable="true" :title="cardTitle(items[id])" @click.stop="selectGame(id)" @dragstart="startDrag($event, id)" @dragend="draggingId = ''" @dragover.prevent @drop.prevent.stop="dropOn($event, 'pool', id)">
          <img :src="items[id].cover" :alt="items[id].name" loading="lazy"><span>{{ items[id].translatedName || items[id].name }}</span><button type="button" aria-label="移除作品" title="移除作品" @click.stop="removeGame(id)">×</button>
        </div>
      </div>
      <div v-if="placements.pool.length > board.poolShown" class="pool-more"><el-button size="small" @click="board.poolShown += 60">再显示 60 部</el-button></div>
    </section>
    <section v-show="workflowStep === 2" class="save-step">
      <div class="section-heading"><div><small>EXPORT RANKING</small><h2>保存图片</h2></div><span>仅导出当前 Tab 已分级作品</span></div>
      <app-scrollbar v-loading="renderingPreview" class="export-preview">
        <img v-if="previewUrl" :src="previewUrl" :alt="`${board.title || '未命名分级'}导出预览`">
        <span v-else-if="previewError">{{ previewError }}</span>
        <span v-else>正在生成导出预览…</span>
      </app-scrollbar>
      <p v-if="previewWarning" class="preview-warning">{{ previewWarning }}</p>
      <div class="export-settings">
        <strong>背景</strong>
        <el-radio-group v-model="exportBackgroundMode" class="export-background-options">
          <el-radio label="current">当前网页背景</el-radio>
          <el-radio label="color">纯色</el-radio>
          <el-radio label="pattern">图案</el-radio>
          <el-radio label="upload">上传图片</el-radio>
        </el-radio-group>
        <div v-if="exportBackgroundMode === 'current'" class="export-background-detail">
          <span>{{ currentBackgroundUrl ? '使用当前网页背景，预览会同步更新。' : '当前网页暂无背景图，将使用默认浅色背景。' }}</span>
        </div>
        <div v-else-if="exportBackgroundMode === 'color'" class="export-background-detail export-color-detail">
          <button v-for="preset in exportColorPresets" :key="preset.color" type="button" class="export-swatch" :class="{ selected: exportBackgroundColor === preset.color }" :style="{ backgroundColor: preset.color }" :title="preset.name" :aria-label="`选择${preset.name}背景`" @click="exportBackgroundColor = preset.color"></button>
          <el-color-picker v-model="exportBackgroundColor" size="small"></el-color-picker>
          <span>自定义 · {{ exportBackgroundColor }}</span>
        </div>
        <div v-else-if="exportBackgroundMode === 'pattern'" class="export-background-detail export-pattern-detail">
          <div class="export-pattern-options">
            <button v-for="pattern in exportPatternPresets" :key="pattern.id" type="button" class="export-pattern-option" :class="[{ selected: exportPattern === pattern.id }, `pattern-${pattern.id}`]" :style="{ '--pattern-color': exportPatternColor, '--pattern-background': exportPatternBackground }" @click="exportPattern = pattern.id"><span>{{ pattern.name }}</span></button>
          </div>
          <div class="export-pattern-controls">
            <label>图案颜色 <el-color-picker v-model="exportPatternColor" size="small"></el-color-picker></label>
            <label>背景颜色 <el-color-picker v-model="exportPatternBackground" size="small"></el-color-picker></label>
          </div>
        </div>
        <div v-else class="export-background-detail export-upload-detail">
          <input ref="exportImageInput" type="file" accept="image/jpeg,image/png,image/webp" class="export-file-input" @change="chooseExportImage">
          <el-button size="small" @click="$refs.exportImageInput.click()">选择图片</el-button>
          <span v-if="uploadedImageName" class="export-file-name" :title="uploadedImageName">{{ uploadedImageName }}</span>
          <span v-else>支持 JPG、PNG、WebP，最大 10 MB、2400 万像素</span>
        </div>
      </div>
      <el-button type="primary" icon="el-icon-download" :disabled="!canConfirmExport" @click="exportCurrentTab">导出</el-button>
    </section>
      </app-scrollbar>
      <div class="workflow-footer">
        <el-button size="small" :disabled="workflowStep === 0" @click="goStep(workflowStep - 1)">上一步</el-button>
        <span>{{ workflowStep === 1 && !canExport ? exportHint : `第 ${workflowStep + 1} / 3 步` }}</span>
        <span v-if="workflowStep < 2" :title="workflowStep === 1 ? exportHint : ''"><el-button size="small" type="primary" :disabled="workflowStep === 1 && !canExport" @click="goStep(workflowStep + 1)">下一步</el-button></span>
        <el-button v-else size="small" :disabled="true">下一步</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppScrollbar from '@/components/AppScrollbar.vue'
import { renderTierBoardImage, downloadTierBoardImage } from '@/utils/tierBoardExport'

const defaultTiers = () => [
  { id: 'S', label: 'S', color: '#f27b84' },
  { id: 'A', label: 'A', color: '#f5aa68' },
  { id: 'B', label: 'B', color: '#f3ce71' },
  { id: 'C', label: 'C', color: '#91cda2' },
  { id: 'D', label: 'D', color: '#91bce7' }
]
const emptyFilters = () => ({ name: '', gameId: '', author: null, type: null, releaseTime: null, tags: [] })
const dateShortcutLabels = ['今天', '昨天', '一周前', '本月', '上月', '本季', '今年', '去年']
const dateRangeFor = (label) => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()
  if (label === '今天') return [now, now]
  if (label === '昨天' || label === '一周前') {
    const start = new Date(now)
    start.setTime(start.getTime() - 24 * 60 * 60 * 1000 * (label === '昨天' ? 1 : 7))
    return [start, label === '昨天' ? start : now]
  }
  if (label === '本月') return [new Date(year, month, 1), new Date(year, month + 1, 0)]
  if (label === '上月') return [new Date(year, month - 1, 1), new Date(year, month, 0)]
  if (label === '本季') {
    const quarterStart = Math.floor(month / 3) * 3
    return [new Date(year, quarterStart, 1), new Date(year, quarterStart + 3, 0)]
  }
  if (label === '今年') return [new Date(year, 0, 1), new Date(year, 11, 31)]
  return [new Date(year - 1, 0, 1), new Date(year - 1, 11, 31)]
}
const formatLocalDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
const storageKey = 'dlsite-tier-board-v1'
const createBoard = (id, title) => ({
  id, title, step: 0, tiers: defaultTiers(), tierCounter: 0, items: {},
  placements: { pool: [], S: [], A: [], B: [], C: [], D: [] }, poolShown: 60
})
const validTierId = id => typeof id === 'string' && /^(?:[A-D]|S|custom-[0-9]+)$/.test(id)
const restoreBoard = (source) => {
  if (!source || typeof source.id !== 'string' || !/^board-[0-9]+$/.test(source.id)) return null
  const board = createBoard(source.id, typeof source.title === 'string' ? source.title.slice(0, 60) : '我的分级')
  if (Array.isArray(source.tiers) && source.tiers.length && source.tiers.length <= 30) {
    const seen = new Set()
    const tiers = source.tiers.filter(tier => tier && validTierId(tier.id) && !seen.has(tier.id) && typeof tier.label === 'string' && /^#[0-9a-fA-F]{6}$/.test(tier.color)).map(tier => {
      seen.add(tier.id)
      return { id: tier.id, label: tier.label.slice(0, 12), color: tier.color }
    })
    if (tiers.length) board.tiers = tiers
  }
  board.tierCounter = Math.max(...board.tiers.map(tier => Number((tier.id.match(/^custom-([0-9]+)$/) || [])[1]) || 0), 0)
  if (source.items && typeof source.items === 'object' && !Array.isArray(source.items)) {
    Object.keys(source.items).forEach(id => {
      const game = source.items[id]
      if (!/^(?:[0-9]+|rpc:RJ[0-9]{6,8})$/.test(id) || !game || typeof game !== 'object') return
      board.items[id] = {
        id, gameId: String(game.gameId || ''), name: String(game.name || ''),
        translatedName: String(game.translatedName || ''), cover: String(game.cover || ''),
        author: String(game.author || ''), external: id.startsWith('rpc:')
      }
    })
  }
  const placed = new Set()
  const sourcePlacements = source.placements && typeof source.placements === 'object' ? source.placements : {}
  board.placements = {}
  ;['pool', ...board.tiers.map(tier => tier.id)].forEach(key => {
    const ids = Array.isArray(sourcePlacements[key]) ? sourcePlacements[key] : []
    board.placements[key] = ids.filter(id => {
      if (!Object.prototype.hasOwnProperty.call(board.items, id) || placed.has(id)) return false
      placed.add(id)
      return true
    })
  })
  Object.keys(board.items).forEach(id => { if (!placed.has(id)) board.placements.pool.push(id) })
  board.poolShown = Math.max(60, Math.min(Number(source.poolShown) || 60, 10000))
  board.step = Number.isInteger(source.step) && source.step >= 0 && source.step <= 2 ? source.step : 0
  if (board.step === 2 && Object.keys(board.items).length === board.placements.pool.length) board.step = 1
  return board
}

export default {
  name: 'LabTierBoard',
  components: { AppScrollbar },
  inject: ['backgroundOwner'],
  data() {
    return {
      allowed: false, filters: emptyFilters(), makers: [], types: [], genres: [], tagProps: { multiple: true, emitPath: false }, isMobile: false,
      pickerOptions: { shortcuts: dateShortcutLabels.map(text => ({ text, onClick(picker) { picker.$emit('pick', dateRangeFor(text)) } })) },
      results: [], externalResult: null, resultTotal: 0, resultPage: 1, searched: false, searching: false, requestId: 0, lastQuery: null,
      bulkAdding: false, bulkProgress: 0, cancelBulk: false,
      tabs: [createBoard('board-1', '我的分级')], activeTabId: 'board-1', tabCounter: 1,
      storageReady: false, storageError: false, saveTimer: null,
      selectedId: '', draggingId: '',
      previewUrl: '', previewBlob: null, previewError: '', previewWarning: '', renderingPreview: false,
      previewTimer: null, previewRequestId: 0,
      exportBackgroundMode: 'current', exportBackgroundColor: '#f4f7fb', exportPattern: 'dots', exportPatternColor: '#77a9db', exportPatternBackground: '#f3f7fc',
      exportColorPresets: [
        { name: '雾白', color: '#f4f7fb' }, { name: '暖粉', color: '#fff0f3' },
        { name: '浅蓝', color: '#e9f3ff' }, { name: '薄荷', color: '#e9f8f1' },
        { name: '深夜', color: '#273345' }
      ],
      exportPatternPresets: [
        { id: 'dots', name: '圆点' }, { id: 'grid', name: '方格' }, { id: 'diagonal', name: '斜纹' },
        { id: 'checker', name: '棋盘' }, { id: 'waves', name: '波纹' }, { id: 'stars', name: '星点' }
      ],
      uploadedImageUrl: '', uploadedImageName: '', uploadRequestId: 0
    }
  },
  computed: {
    board() { return this.tabs.find(tab => tab.id === this.activeTabId) || this.tabs[0] },
    workflowStep() { return this.board.step },
    tiers() { return this.board.tiers },
    items() { return this.board.items },
    placements() { return this.board.placements },
    displayResults() { return this.resultPage === 1 && this.externalResult ? [this.externalResult, ...this.results] : this.results },
    displayTotal() { return this.resultTotal + (this.externalResult ? 1 : 0) },
    mobileStartDate: {
      get() { return this.filters.releaseTime ? this.filters.releaseTime[0] : null },
      set(value) {
        if (!value) { this.filters.releaseTime = null; return }
        const end = this.mobileEndDate
        this.filters.releaseTime = [value, end && end >= value ? end : value]
      }
    },
    mobileEndDate: {
      get() { return this.filters.releaseTime ? this.filters.releaseTime[1] : null },
      set(value) {
        if (!value) { this.filters.releaseTime = null; return }
        const start = this.mobileStartDate
        this.filters.releaseTime = [start && start <= value ? start : value, value]
      }
    },
    totalAdded() { return Object.keys(this.items).length },
    rankedCount() { return this.totalAdded - this.placements.pool.length },
    canExport() { return this.rankedCount > 0 },
    canConfirmExport() { return this.canExport && this.workflowStep === 2 && Boolean(this.previewBlob) && !this.renderingPreview && (this.exportBackgroundMode !== 'upload' || Boolean(this.uploadedImageUrl)) },
    currentBackgroundUrl() { return this.backgroundOwner.background && this.backgroundOwner.background.url || '' },
    exportHint() {
      if (!this.rankedCount) return '请先将至少 1 部作品放入分级区'
      return '仅导出当前分级板中已分级的作品'
    },
    visiblePool() { return this.placements.pool.slice(0, this.board.poolShown) }
  },
  watch: {
    tabs: { deep: true, handler() { this.scheduleSave(); this.schedulePreview() } },
    activeTabId() { this.scheduleSave(); this.schedulePreview() },
    workflowStep(step) { if (step === 2) this.schedulePreview(); else this.cleanupPreview() },
    exportBackgroundMode() { this.schedulePreview() },
    exportBackgroundColor() { this.schedulePreview() },
    exportPattern() { this.schedulePreview() },
    exportPatternColor() { this.schedulePreview() },
    exportPatternBackground() { this.schedulePreview() },
    uploadedImageUrl() { this.schedulePreview() },
    currentBackgroundUrl() { this.schedulePreview() }
  },
  created() { this.restoreBoards(); this.checkAccess() },
  mounted() { this.updateViewport(); window.addEventListener('resize', this.updateViewport); window.addEventListener('pagehide', this.saveBoards) },
  beforeDestroy() {
    this.cancelBulk = true
    this.requestId++
    this.cleanupUpload()
    this.cleanupPreview()
    window.removeEventListener('resize', this.updateViewport)
    window.removeEventListener('pagehide', this.saveBoards)
    clearTimeout(this.saveTimer)
    this.saveBoards()
  },
  methods: {
    goStep(step) {
      if (step < 0 || step > 2 || (step === 2 && !this.canExport)) return
      this.board.step = step
      this.$nextTick(() => { if (this.$refs.workflowBody) this.$refs.workflowBody.$el.scrollTop = 0 })
    },
    exportBackground() {
      if (this.exportBackgroundMode === 'current') return { type: 'current', ...(this.backgroundOwner.background || {}) }
      if (this.exportBackgroundMode === 'color') return { type: 'color', color: this.exportBackgroundColor }
      if (this.exportBackgroundMode === 'pattern') return { type: 'pattern', pattern: this.exportPattern, color: this.exportPatternColor, backgroundColor: this.exportPatternBackground }
      return { type: 'upload', url: this.uploadedImageUrl }
    },
    cleanupPreview() {
      this.previewRequestId++
      clearTimeout(this.previewTimer)
      this.previewTimer = null
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      this.previewUrl = ''
      this.previewBlob = null
      this.previewError = ''
      this.previewWarning = ''
      this.renderingPreview = false
    },
    schedulePreview() {
      if (this.workflowStep !== 2) return
      const requestId = ++this.previewRequestId
      clearTimeout(this.previewTimer)
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      this.previewUrl = ''
      this.previewBlob = null
      this.previewError = ''
      this.previewWarning = ''
      this.renderingPreview = true
      if (this.exportBackgroundMode === 'upload' && !this.uploadedImageUrl) {
        this.previewError = '请选择要用作背景的图片'
        this.renderingPreview = false
        return
      }
      this.previewTimer = setTimeout(() => this.refreshPreview(requestId), 250)
    },
    async refreshPreview(requestId) {
      try {
        const result = await renderTierBoardImage(this.board, this.exportBackground())
        if (requestId !== this.previewRequestId || this.workflowStep !== 2) return
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
        this.previewBlob = result.blob
        this.previewUrl = URL.createObjectURL(result.blob)
        const warnings = []
        if (result.missingBackground) warnings.push('背景图片无法加载，已用默认背景代替')
        if (result.missingCovers) warnings.push(`${result.missingCovers} 张封面已用占位图代替`)
        this.previewWarning = warnings.join('；')
      } catch (error) {
        if (requestId === this.previewRequestId) this.previewError = error.message || '预览生成失败'
      } finally {
        if (requestId === this.previewRequestId) this.renderingPreview = false
      }
    },
    cleanupUpload() {
      this.uploadRequestId++
      if (this.uploadedImageUrl) URL.revokeObjectURL(this.uploadedImageUrl)
      this.uploadedImageUrl = ''
      this.uploadedImageName = ''
    },
    chooseExportImage(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        this.$message.warning('请选择 JPG、PNG 或 WebP 图片')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.$message.warning('图片不能超过 10 MB')
        return
      }
      const requestId = ++this.uploadRequestId
      const url = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        if (requestId !== this.uploadRequestId || this.workflowStep !== 2) { URL.revokeObjectURL(url); return }
        if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 24 * 1000 * 1000 || image.naturalWidth > 8000 || image.naturalHeight > 8000) {
          URL.revokeObjectURL(url)
          this.$message.warning('图片分辨率过高，请使用不超过 2400 万像素的图片')
          return
        }
        this.cleanupUpload()
        this.uploadedImageUrl = url
        this.uploadedImageName = file.name
      }
      image.onerror = () => {
        URL.revokeObjectURL(url)
        if (requestId === this.uploadRequestId) this.$message.warning('无法读取这张图片')
      }
      image.src = url
    },
    exportCurrentTab() {
      if (!this.canConfirmExport) return
      try {
        downloadTierBoardImage(this.previewBlob, this.board.title)
        this.$message.success('分级图片已导出')
      } catch (error) {
        this.$message.error(error.message || '导出失败，请重试')
      }
    },
    restoreBoards() {
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || 'null')
        if (saved && saved.version === 1 && Array.isArray(saved.tabs)) {
          const tabs = saved.tabs.slice(0, 30).map(restoreBoard).filter(Boolean)
          if (tabs.length) {
            this.tabs = tabs
            this.tabCounter = Math.max(...tabs.map(tab => Number((tab.id.match(/^board-([0-9]+)$/) || [])[1]) || 0), 1)
            this.activeTabId = tabs.some(tab => tab.id === saved.activeTabId) ? saved.activeTabId : tabs[0].id
          }
        }
      } catch (error) { /* Invalid or unavailable local storage starts with an empty board. */ }
      this.storageReady = true
    },
    scheduleSave() {
      if (!this.storageReady) return
      clearTimeout(this.saveTimer)
      this.saveTimer = setTimeout(this.saveBoards, 350)
    },
    saveBoards() {
      if (!this.storageReady) return
      clearTimeout(this.saveTimer)
      this.saveTimer = null
      try {
        localStorage.setItem(storageKey, JSON.stringify({ version: 1, activeTabId: this.activeTabId, tabs: this.tabs }))
        this.storageError = false
      } catch (error) { this.storageError = true }
    },
    addTab() {
      if (this.tabs.length >= 30) { this.$message.warning('最多创建 30 个分级板'); return }
      const id = `board-${++this.tabCounter}`
      this.tabs.push(createBoard(id, `分级 ${this.tabs.length + 1}`))
      this.switchTab(id)
      this.goStep(0)
    },
    switchTab(id) {
      if (!this.tabs.some(tab => tab.id === id)) return
      this.cancelBulk = true
      this.selectedId = ''
      this.draggingId = ''
      this.activeTabId = id
      const target = this.tabs.find(tab => tab.id === id)
      if (target.step === 2 && Object.keys(target.items).length === target.placements.pool.length) target.step = 1
      this.$nextTick(() => { if (this.$refs.workflowBody) this.$refs.workflowBody.$el.scrollTop = 0 })
    },
    async removeTab(id) {
      const tab = this.tabs.find(item => item.id === id)
      if (!tab || this.tabs.length <= 1) return
      if (Object.keys(tab.items).length) {
        try { await this.$confirm(`删除“${tab.title || '未命名分级'}”及其中的全部作品？`, '删除分级板', { type: 'warning' }) }
        catch (error) { return }
      }
      this.cancelBulk = true
      this.tabs = this.tabs.filter(item => item.id !== id)
      if (this.activeTabId === id) this.switchTab(this.tabs[0].id)
    },
    updateViewport() { this.isMobile = window.innerWidth <= 767 },
    applyDateShortcut(label) { this.filters.releaseTime = dateRangeFor(label).map(formatLocalDate) },
    async checkAccess() {
      try {
        const token = localStorage.getItem('token')
        const headers = token ? { Authorization: 'Bearer ' + token } : {}
        const response = await axios.get('/api/open/lab/features', { headers })
        const feature = response.data.data.find(item => item.key === 'tier-board')
        if (response.data.code === 200 && feature && feature.visible) {
          this.allowed = true
          this.loadOptions()
          return
        }
      } catch (error) { /* Close the page if visibility cannot be checked. */ }
      this.$message.warning('当前无法访问该实验室项目')
      this.$router.replace('/')
    },
    async loadOptions() {
      const results = await Promise.allSettled([
        axios.get('/api/open/select/maker'), axios.get('/api/open/select/type'), axios.get('/api/open/select/genre')
      ])
      if (results[0].status === 'fulfilled' && results[0].value.data.code === 200) this.makers = results[0].value.data.data || []
      if (results[1].status === 'fulfilled' && results[1].value.data.code === 200) this.types = results[1].value.data.data || []
      if (results[2].status === 'fulfilled' && results[2].value.data.code === 200) this.genres = results[2].value.data.data.genres || []
    },
    limitTags(value) {
      if (value.length > 5) {
        this.filters.tags = value.slice(0, 5)
        this.$message.warning('最多选择 5 个标签')
      }
    },
    buildQuery() {
      return {
        name: this.filters.name || null, gameId: this.filters.gameId || null,
        author: this.filters.author || null, type: this.filters.type || null,
        releaseTime: this.filters.releaseTime,
        tags: [...this.filters.tags], orderBy: 'idDesc'
      }
    },
    search() {
      this.lastQuery = this.buildQuery()
      this.searched = true
      this.externalResult = null
      this.fetchResults(1)
    },
    closeResults() {
      this.searched = false
      this.cancelBulk = true
      this.requestId++
      this.searching = false
    },
    resetFilters() { this.filters = emptyFilters(); this.search() },
    async fetchResults(page) {
      if (!this.lastQuery) return
      const requestId = ++this.requestId
      this.searching = true
      try {
        const response = await axios.post('/api/open/table', { ...this.lastQuery, pageNo: page, pageSize: 24 })
        if (requestId !== this.requestId) return
        if (response.data.code !== 200) throw new Error('搜索失败')
        this.results = response.data.data.rows || []
        this.resultTotal = response.data.data.total || 0
        this.resultPage = page
        const rjId = String(this.lastQuery.gameId || '').trim().toUpperCase()
        if (page === 1 && /^RJ[0-9]{6,8}$/.test(rjId) && !this.results.some(game => String(game.gameId).toUpperCase() === rjId)) {
          try {
            const lookup = await axios.get(`/api/open/lab/tier-board/lookup/${rjId}`)
            if (requestId !== this.requestId) return
            if (lookup.data.code !== 200) throw new Error(lookup.data.message)
            this.externalResult = lookup.data.data || null
          } catch (error) {
            if (requestId === this.requestId) this.$message.warning('本站未找到该 RJ 编号，DLsite 查询失败，请稍后重试')
          }
        }
      } catch (error) { if (requestId === this.requestId) this.$message.error('搜索失败，请重试') }
      finally { if (requestId === this.requestId) this.searching = false }
    },
    addRows(rows, board = this.board) {
      let count = 0
      rows.forEach(game => {
        const id = String(game.id)
        if (!/^(?:[0-9]+|rpc:RJ[0-9]{6,8})$/.test(id) || board.items[id]) return
        this.$set(board.items, id, {
          id, gameId: String(game.gameId || ''), name: String(game.name || ''),
          translatedName: String(game.translatedName || ''), cover: String(game.cover || ''),
          author: String(game.author || ''), external: Boolean(game.external)
        })
        board.placements.pool.push(id)
        count++
      })
      return count
    },
    addCurrentPage() {
      const count = this.addRows(this.displayResults)
      this.$message.success(count ? `已加入 ${count} 部作品` : '本页作品已全部加入')
    },
    async addAllMatches() {
      if (!this.lastQuery || this.bulkAdding) return
      const targetBoard = this.board
      const query = { ...this.lastQuery, tags: [...this.lastQuery.tags] }
      const total = this.resultTotal
      const external = this.externalResult
      if (total > 500) {
        try { await this.$confirm(`将 ${total.toLocaleString()} 部作品加入候选区？数量较多时需要一些时间。`, '批量添加', { type: 'info' }) }
        catch (error) { return }
      }
      this.bulkAdding = true
      this.cancelBulk = false
      this.bulkProgress = 0
      let added = external ? this.addRows([external], targetBoard) : 0
      try {
        for (let page = 1; page <= Math.ceil(total / 200) && !this.cancelBulk; page++) {
          const response = await axios.post('/api/open/table', { ...query, pageNo: page, pageSize: 200 })
          if (this.cancelBulk || !this.tabs.includes(targetBoard)) break
          if (response.data.code !== 200) throw new Error('批量加载失败')
          const rows = response.data.data.rows || []
          added += this.addRows(rows, targetBoard)
          this.bulkProgress += rows.length
          if (!rows.length) break
        }
        this.$message.success(`已加入 ${added} 部作品${this.cancelBulk ? '（已停止）' : ''}`)
      } catch (error) { this.$message.error(`批量添加中断，已加入 ${added} 部作品`) }
      finally { this.bulkAdding = false }
    },
    cardTitle(game) { return `${game.translatedName || game.name}\n${game.gameId}${game.external ? ' · 未收录' : ' · ' + game.id}` },
    selectGame(id) { this.selectedId = this.selectedId === id ? '' : id },
    placeSelected(tierId) { if (this.selectedId) this.moveGame(this.selectedId, tierId) },
    startDrag(event, id) {
      this.draggingId = id
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', id)
    },
    dropOn(event, tierId, beforeId = '') {
      const id = event.dataTransfer.getData('text/plain') || this.draggingId
      if (id && this.items[id]) this.moveGame(id, tierId, beforeId)
      this.draggingId = ''
    },
    moveGame(id, tierId, beforeId = '') {
      if (!this.items[id] || !this.placements[tierId] || id === beforeId) return
      Object.keys(this.placements).forEach(key => {
        const index = this.placements[key].indexOf(id)
        if (index !== -1) this.placements[key].splice(index, 1)
      })
      const target = this.placements[tierId]
      const index = beforeId ? target.indexOf(beforeId) : -1
      target.splice(index < 0 ? target.length : index, 0, id)
      this.selectedId = ''
    },
    removeGame(id) {
      Object.keys(this.placements).forEach(key => {
        const index = this.placements[key].indexOf(id)
        if (index !== -1) this.placements[key].splice(index, 1)
      })
      this.$delete(this.items, id)
      if (this.selectedId === id) this.selectedId = ''
    },
    addTier() {
      const id = `custom-${++this.board.tierCounter}`
      this.tiers.push({ id, label: '新等级', color: '#b9a4dc' })
      this.$set(this.placements, id, [])
    },
    removeTier(id) {
      if (this.tiers.length === 1) { this.$message.warning('至少保留一个等级'); return }
      this.placements.pool.push(...this.placements[id])
      this.$delete(this.placements, id)
      this.board.tiers = this.tiers.filter(tier => tier.id !== id)
    },
    resetPlacements() {
      this.tiers.forEach(tier => {
        this.placements.pool.push(...this.placements[tier.id])
        this.placements[tier.id] = []
      })
      this.selectedId = ''
    },
    async clearBoard() {
      const board = this.board
      try { await this.$confirm('清空当前分级板的作品并返回添加步骤？', '重选作品', { type: 'warning' }) }
      catch (error) { return }
      if (!this.tabs.includes(board) || this.board !== board) return
      board.items = {}
      Object.keys(board.placements).forEach(key => { board.placements[key] = [] })
      this.selectedId = ''
      board.poolShown = 60
      this.goStep(0)
    }
  }
}
</script>

<style scoped>
.tier-page { display: flex; flex-direction: column; gap: 10px; height: 100%; min-height: 0; color: #303133; text-align: left; }
.glass-card { border: 1px solid rgba(220, 223, 230, .72); border-radius: 16px; background: rgba(255, 255, 255, .68); backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px); box-shadow: 0 12px 35px rgba(45, 58, 75, .07); }
.tier-heading { display: flex; flex: none; justify-content: space-between; gap: 16px; align-items: flex-start; padding: 15px 20px; }
small { color: #409eff; font-size: 11px; font-weight: 700; letter-spacing: .12em; }
h1, h2, p { margin: 0; }
h1 { margin-top: 3px; font-size: 23px; } h2 { margin-top: 4px; font-size: 19px; }
.tier-heading p { margin-top: 9px; color: #697483; font-size: 13px; line-height: 1.5; }
.session-note { color: #697483; font-size: 12px; white-space: nowrap; padding-top: 5px; }
.tier-workflow { display: flex; flex: 1; flex-direction: column; min-height: 0; overflow: hidden; }
.workflow-steps { flex: none; padding: 15px 22px 6px; }
.workflow-steps >>> .el-step__title { font-size: 13px; line-height: 22px; }
.workflow-steps >>> .el-step__icon { width: 25px; height: 25px; }
.workflow-body { flex: 1; min-height: 0; }
.workflow-footer { display: flex; flex: none; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 20px; border-top: 1px solid rgba(220,223,230,.85); background: rgba(255,255,255,.58); }
.workflow-footer span { color: #8c97a5; font-size: 12px; }
.finder, .board { padding: 18px 20px; }
.finder { display: flex; flex-direction: column; height: 100%; min-height: 650px; box-sizing: border-box; }
.section-heading { display: flex; align-items: end; justify-content: space-between; gap: 10px; margin-bottom: 15px; }
.section-heading > span { color: #9199a3; font-size: 12px; }
.section-heading em { color: #8c97a5; font-size: 13px; font-style: normal; font-weight: 400; }
.filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px 14px; }
.filter-grid label { display: flex; flex-direction: column; min-width: 0; gap: 6px; color: #697483; font-size: 12px; }
.filter-grid .el-select, .filter-grid .el-cascader { width: 100%; }
.release-filter { grid-column: span 1; }
.release-filter >>> .el-date-editor { width: 100%; }
.release-filter >>> .el-date-editor.el-range-editor { background: rgba(255, 255, 255, .55); }
.release-filter >>> .el-date-editor .el-range-input { background: transparent; }
.filter-actions { display: flex; gap: 8px; align-items: center; margin-top: 16px; }
.filter-actions .el-button + .el-button { margin-left: 0; }
.result-count { margin-left: auto; color: #909399; font-size: 12px; }
.results { display: flex; flex: 1 1 0; flex-direction: column; min-height: 0; margin-top: 14px; border-top: 1px solid rgba(220, 223, 230, .8); padding-top: 10px; }
.results-toolbar, .board-actions, .pool-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.results-toolbar strong { font-size: 14px; } .results-toolbar .el-button + .el-button { margin-left: 5px; }
.result-scroll { flex: 1 1 0; min-height: 0; margin: 8px 0; }
.result-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; min-height: 52px; }
.result-item { display: flex; align-items: center; gap: 9px; min-width: 0; padding: 7px; border: 1px solid rgba(220, 223, 230, .8); border-radius: 9px; background: rgba(255, 255, 255, .5); }
.result-item img { flex: none; width: 46px; height: 54px; border-radius: 5px; object-fit: cover; background: #e5e9f0; }
.result-info { flex: 1; min-width: 0; } .result-info strong, .result-info span, .result-info small { display: block; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.result-info strong { font-size: 12px; } .result-info span { margin-top: 3px; color: #6a7280; font-size: 11px; } .result-info small { margin-top: 3px; color: #8a94a3; font-size: 10px; font-weight: 400; letter-spacing: 0; }
.result-info .external-badge { display: inline; margin: 0 5px 0 0; padding: 1px 4px; border-radius: 3px; background: rgba(64, 158, 255, .13); color: #2785d9; font-size: 10px; font-weight: 600; }
.empty-results { grid-column: 1 / -1; align-self: center; color: #9aa3af; text-align: center; font-size: 13px; }
.results >>> .el-pagination { text-align: center; }
.selected-works { display: flex; flex: 1 1 0; flex-direction: column; min-height: 0; margin-top: 10px; }
.selected-heading { display: flex; justify-content: space-between; gap: 8px; margin-bottom: 10px; padding-top: 13px; border-top: 1px solid rgba(220,223,230,.85); }
.selected-heading strong { font-size: 14px; } .selected-heading em { color: #409eff; font-size: 12px; font-style: normal; }
.selected-heading span, .selected-empty { color: #8793a1; font-size: 12px; }
.selected-scroll { flex: 1 1 0; min-height: 0; }
.selected-grid { display: flex; flex-wrap: wrap; align-content: flex-start; gap: 5px; min-height: 100%; box-sizing: border-box; padding: 7px; border: 1px dashed #b8c7d8; border-radius: 9px; background: rgba(255,255,255,.35); }
.selected-empty { padding: 15px; text-align: center; }
.board-tabs { display: flex; flex: none; align-items: stretch; gap: 6px; overflow-x: auto; padding: 10px 20px 12px; border-bottom: 1px solid rgba(155, 171, 190, .65); scrollbar-width: thin; }
.board-tab { display: flex; flex: none; align-items: center; max-width: 220px; border: 1px solid rgba(213, 222, 232, .9); border-radius: 8px; background: rgba(255, 255, 255, .45); }
.board-tab.active { color: #2386e5; border-color: rgba(64, 158, 255, .65); background: rgba(64, 158, 255, .1); }
.board-tab button, .add-tab { border: 0; background: none; color: inherit; cursor: pointer; }
.board-tab button:first-child { display: block; min-width: 0; overflow: hidden; padding: 9px 6px 9px 12px; white-space: nowrap; text-overflow: ellipsis; font-size: 13px; font-weight: 600; }
.board-tab span { margin-left: 4px; color: #8995a3; font-size: 11px; font-weight: 400; }
.board-tab .tab-close { flex: none; padding: 6px 10px 6px 4px; color: #8d98a5; font-size: 17px; }
.board-tab .tab-close:hover { color: #f56c6c; }
.add-tab { flex: none; padding: 0 10px; color: #409eff; font-size: 12px; }
.board-heading { flex-wrap: wrap; }
.board-actions { flex-wrap: wrap; justify-content: flex-start; } .board-actions .el-button + .el-button { margin-left: 0; }
.board-title { display: flex; align-items: center; gap: 10px; max-width: 450px; margin-bottom: 16px; color: #697483; white-space: nowrap; font-size: 12px; }
.board-title .el-input { min-width: 0; }
.storage-error { margin: 0 0 12px; color: #e65d5d; font-size: 12px; }
.selection-hint { margin: 0 0 12px; padding: 8px 10px; border-radius: 7px; background: rgba(64, 158, 255, .1); color: #3a84c5; font-size: 12px; }
.selection-hint button { border: 0; background: none; color: inherit; cursor: pointer; text-decoration: underline; }
.tier-rows { display: flex; flex-direction: column; gap: 5px; }
.tier-row { display: grid; grid-template-columns: 92px minmax(0, 1fr); min-height: 126px; border: 1px solid rgba(220, 223, 230, .9); border-radius: 9px; overflow: hidden; }
.tier-label { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 9px; padding: 8px; background: var(--tier-color); cursor: pointer; }
.tier-label input[type=text], .tier-label input:not([type]) { width: 100%; box-sizing: border-box; border: 0; outline: none; background: transparent; text-align: center; color: #23303c; font-weight: 800; font-size: 22px; }
.tier-label input[type=color] { width: 28px; height: 20px; padding: 0; border: 0; background: transparent; cursor: pointer; }
.tier-label button { border: 0; background: rgba(255,255,255,.32); border-radius: 50%; cursor: pointer; font-size: 16px; width: 22px; height: 22px; line-height: 20px; }
.tier-drop, .pool-drop { display: flex; align-content: flex-start; align-items: flex-start; flex-wrap: wrap; gap: 5px; padding: 7px; min-width: 0; background: rgba(255, 255, 255, .28); cursor: pointer; }
.drop-placeholder, .pool-empty { align-self: center; width: 100%; color: #a7b0bc; font-size: 12px; text-align: center; }
.rank-card { position: relative; flex: none; width: 83px; height: 110px; border: 2px solid transparent; border-radius: 6px; overflow: hidden; background: #e5e9ef; cursor: grab; }
.rank-card.selected { border-color: #409eff; box-shadow: 0 0 0 2px rgba(64, 158, 255, .22); }
.rank-card img { display: block; width: 100%; height: 100%; object-fit: cover; }
.rank-card span { position: absolute; right: 0; bottom: 0; left: 0; padding: 12px 4px 4px; color: #fff; background: linear-gradient(transparent, rgba(0,0,0,.8)); font-size: 10px; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rank-card button { position: absolute; top: 2px; right: 2px; display: grid; place-items: center; width: 19px; height: 19px; padding: 0; border: 0; border-radius: 50%; background: rgba(19,29,43,.78); color: white; cursor: pointer; font-size: 15px; line-height: 1; }
.pool-heading { margin: 18px 0 9px; color: #707d89; font-size: 12px; } .pool-heading strong { margin-right: 8px; color: #303133; font-size: 15px; }
.pool-drop { min-height: 130px; border: 1px dashed #b8c7d8; border-radius: 9px; background: rgba(255,255,255,.35); }
.pool-more { margin-top: 9px; text-align: center; }
.save-step { padding: 20px; }
.export-preview { display: block; width: min(100%, 700px); max-height: min(45vh, 460px); min-height: 180px; margin: 0 auto 16px; border: 1px solid rgba(190,205,220,.9); border-radius: 10px; background: #f4f7fb; text-align: center; }
.export-preview img { display: block; width: 100%; height: auto; }
.export-preview > span { display: block; padding: 70px 12px; color: #8793a1; font-size: 13px; }
.preview-warning { margin: 0 0 12px; color: #e6a23c; font-size: 12px; }
.export-settings { margin-bottom: 16px; }
.export-settings > strong { display: block; margin-bottom: 10px; font-size: 14px; }
.export-background-options { display: flex; flex-wrap: wrap; gap: 10px 16px; }
.export-background-options >>> .el-radio { margin: 0; }
.export-background-detail { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; min-height: 60px; margin-top: 18px; padding: 14px; border: 1px solid #e0e7ef; border-radius: 10px; background: #f8fafc; color: #798594; font-size: 12px; }
.export-background-detail > img { display: block; width: 100%; max-height: 160px; border-radius: 7px; object-fit: cover; }
.export-color-detail { min-height: 42px; }
.export-swatch { width: 30px; height: 30px; padding: 0; border: 2px solid #fff; border-radius: 50%; box-shadow: 0 0 0 1px #c5d1de; cursor: pointer; }
.export-swatch.selected { box-shadow: 0 0 0 2px #409eff; }
.export-color-detail >>> .el-color-picker { margin-left: 5px; }
.export-pattern-detail { flex-direction: column; align-items: stretch; gap: 12px; }
.export-pattern-options, .export-pattern-controls { display: flex; flex-wrap: wrap; gap: 10px; }
.export-pattern-controls label { display: flex; align-items: center; gap: 8px; }
.export-pattern-option { display: grid; place-items: end center; width: 104px; height: 76px; padding: 0 0 6px; border: 2px solid #d9e3ee; border-radius: 8px; color: #405366; cursor: pointer; font-size: 12px; }
.export-pattern-option.selected { border-color: #409eff; box-shadow: 0 0 0 2px rgba(64,158,255,.15); }
.pattern-dots { background-color: var(--pattern-background); background-image: radial-gradient(var(--pattern-color) 2px, transparent 2px); background-size: 20px 20px; }
.pattern-grid { background-color: var(--pattern-background); background-image: linear-gradient(var(--pattern-color) 1px, transparent 1px), linear-gradient(90deg, var(--pattern-color) 1px, transparent 1px); background-size: 20px 20px; }
.pattern-diagonal { background: repeating-linear-gradient(45deg, var(--pattern-background), var(--pattern-background) 13px, var(--pattern-color) 14px, var(--pattern-background) 15px); }
.pattern-checker { background-color: var(--pattern-background); background-image: conic-gradient(var(--pattern-color) 25%, transparent 0 50%, var(--pattern-color) 0 75%, transparent 0); background-size: 32px 32px; }
.pattern-waves { background-color: var(--pattern-background); background-image: repeating-radial-gradient(ellipse at 50% 0, transparent 0 9px, var(--pattern-color) 10px, transparent 11px 20px); }
.pattern-stars { background-color: var(--pattern-background); background-image: radial-gradient(var(--pattern-color) 2px, transparent 3px), radial-gradient(var(--pattern-color) 1px, transparent 2px); background-position: 0 0, 13px 13px; background-size: 26px 26px; }
.export-pattern-option span { padding: 2px 8px; border-radius: 4px; background: rgba(255,255,255,.88); }
.export-file-input { display: none; }
.export-file-name { max-width: calc(100% - 100px); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
@media (max-width: 767px) {
  .tier-heading { display: block; padding: 10px 13px; } .tier-heading h1 { font-size: 19px; } .tier-heading p { display: none; } .session-note { display: block; margin-top: 4px; font-size: 10px; }
  .workflow-steps { padding: 10px 6px 4px; } .workflow-steps >>> .el-step__title { font-size: 11px; white-space: nowrap; }
  .board-tabs { padding: 4px 12px 7px; }
  .workflow-footer { padding: 8px 12px; }
  .finder, .board { padding: 15px 12px; }
  .save-step { padding: 15px 12px; } .export-preview { max-height: 38vh; }
  .finder { min-height: 690px; }
  .results, .selected-works { min-height: 125px; }
  .filter-grid { grid-template-columns: repeat(2, minmax(0,1fr)); gap: 10px; }
  .filter-grid label:nth-child(1), .filter-grid label:nth-child(2), .filter-grid label:nth-child(3), .filter-grid label:nth-child(4) { grid-column: 1 / -1; }
  .release-filter { grid-column: 1 / -1; }
  .mobile-date-range { display: flex; gap: 8px; min-width: 0; }
  .mobile-date-range >>> .el-date-editor { flex: 1; width: calc(50% - 4px) !important; min-width: 0; }
  .mobile-date-shortcuts { display: flex; gap: 5px; margin-top: 7px; padding-bottom: 2px; overflow-x: auto; scrollbar-width: thin; scrollbar-color: #409eff rgba(220,223,230,.5); }
  .mobile-date-shortcuts >>> .el-button { flex: none; margin: 0; padding: 5px 8px; }
  .result-list { grid-template-columns: 1fr; } .results-toolbar { align-items: flex-start; flex-wrap: wrap; }
  .tier-row { grid-template-columns: 66px minmax(0,1fr); min-height: 105px; }
  .tier-label { gap: 5px; padding: 4px; } .tier-label input[type=text], .tier-label input:not([type]) { font-size: 18px; }
  .rank-card { width: 68px; height: 91px; } .tier-drop { padding: 5px; }
  .pool-heading > span { display: none; }
}
</style>

<style>
@media (max-width: 767px) {
  .tier-mobile-date-popper.el-picker-panel { left: max(10px, calc(50vw - 162px)) !important; max-width: calc(100vw - 20px); }
}
</style>
